import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Building2, Zap, Crown, ArrowRight, Shield, BarChart3, Bell, Users, FileText, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'control',
    name: 'Control',
    description: 'Operación organizada y transparente',
    icon: Building2,
    color: 'border-zinc-600',
    bgGradient: 'from-zinc-800 to-zinc-900',
    monthly: 49,
    annual: 470,
    maxUsers: 3,
    extraUserPrice: 10,
    features: [
      { text: '1 fraccionamiento', icon: Building2 },
      { text: 'Lotes ilimitados', icon: Check },
      { text: '3 usuarios incluidos', icon: Users },
      { text: 'Dashboard financiero general', icon: BarChart3 },
      { text: 'Registro de pagos (efectivo, transferencia, tarjeta)', icon: FileText },
      { text: 'Estado de cuenta por cliente', icon: FileText },
      { text: 'Inventario por manzana', icon: Building2 },
      { text: 'Historial y papelera', icon: Clock },
      { text: 'Reporte básico de ventas', icon: FileText },
      { text: 'Soporte estándar', icon: Shield }
    ]
  },
  {
    id: 'profesional',
    name: 'Profesional',
    description: 'Control financiero avanzado',
    icon: Zap,
    color: 'border-amber-500',
    bgGradient: 'from-amber-900/20 to-zinc-900',
    recommended: true,
    monthly: 89,
    annual: 854,
    maxUsers: 5,
    extraUserPrice: 8,
    features: [
      { text: 'Hasta 3 fraccionamientos', icon: Building2 },
      { text: 'Lotes ilimitados', icon: Check },
      { text: '5 usuarios incluidos', icon: Users },
      { text: 'Todo lo del plan Control', icon: Check, highlight: true },
      { text: 'Flujo mensual proyectado', icon: BarChart3 },
      { text: 'Reporte detallado por vendedor', icon: FileText },
      { text: 'Cálculo automático de comisiones', icon: FileText },
      { text: 'KPIs financieros', icon: BarChart3 },
      { text: 'Notificaciones automáticas de pagos', icon: Bell },
      { text: 'Exportación PDF', icon: FileText },
      { text: 'Historial con trazabilidad', icon: Clock },
      { text: 'Soporte prioritario', icon: Shield }
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Plataforma corporativa completa',
    icon: Crown,
    color: 'border-purple-500',
    bgGradient: 'from-purple-900/20 to-zinc-900',
    monthly: 149,
    annual: 1430,
    maxUsers: 10,
    extraUserPrice: 7,
    features: [
      { text: 'Fraccionamientos ilimitados', icon: Building2 },
      { text: 'Lotes ilimitados', icon: Check },
      { text: '10 usuarios incluidos', icon: Users },
      { text: 'Todo lo del plan Profesional', icon: Check, highlight: true },
      { text: 'Control granular de permisos', icon: Shield },
      { text: 'Panel especial para dueños (solo visualización)', icon: Users },
      { text: 'Auditoría completa (quién, qué, cuándo)', icon: Clock },
      { text: 'Exportación avanzada (Excel/CSV)', icon: FileText },
      { text: 'Branding personalizado (logo empresa)', icon: Building2 },
      { text: 'Onboarding dedicado', icon: Users },
      { text: 'Backup avanzado', icon: Shield },
      { text: 'Soporte prioritario 24/7', icon: Shield }
    ]
  }
];

export default function Precios() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [user, setUser] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        fetchPlanInfo();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };

  const fetchPlanInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/account/plan-info`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(data);
      }
    } catch (error) {
      console.error('Error fetching plan info:', error);
    }
  };

  const [processingPlan, setProcessingPlan] = useState(null);

  const handleSelectPlan = async (planId) => {
    setProcessingPlan(planId);
    const cycle = billingCycle === 'annual' ? 'yearly' : 'monthly';
    
    if (!user) {
      // Guest checkout — directo a Stripe sin cuenta
      try {
        const res = await fetch(`${BACKEND_URL}/api/subscription/guest-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ origin_url: window.location.origin, plan: planId, billing_cycle: cycle })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Error al procesar');
        }
        const data = await res.json();
        window.location.href = data.checkout_url;
        return;
      } catch (error) {
        toast.error(error.message || 'Error al iniciar el pago');
        setProcessingPlan(null);
        return;
      }
    }
    
    // Logged in — checkout con cuenta
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin, plan: planId, billing_cycle: cycle })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al procesar');
      }
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message || 'Error al procesar el pago');
      setProcessingPlan(null);
    }
  };

  const getPrice = (plan) => {
    return billingCycle === 'annual' ? plan.annual : plan.monthly;
  };

  const getSavings = (plan) => {
    const monthlyTotal = plan.monthly * 12;
    return monthlyTotal - plan.annual;
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png" 
            alt="Ibañez"
            className="h-10 cursor-pointer"
            onClick={() => navigate(user ? '/dashboard' : '/login')}
          />
          {user ? (
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-amber-500 hover:bg-amber-400 text-black"
            >
              Ir al Dashboard
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-zinc-400 hover:text-white"
              >
                Iniciar Sesión
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-amber-500 hover:bg-amber-400 text-black"
              >
                Comenzar Prueba Gratis
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-amber-500/20 text-amber-500 mb-4">
            7 días de prueba gratis
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Control y Transparencia para tu<br />
            <span className="text-amber-500">Desarrollo Inmobiliario</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Sistema de administración empresarial diseñado para desarrolladores que buscan 
            control financiero, transparencia operativa y auditoría completa.
          </p>
        </div>
      </section>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-12">
        <div className="bg-zinc-900 p-1 rounded-xl inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              billingCycle === 'monthly' 
                ? 'bg-amber-500 text-black' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Mensual
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              billingCycle === 'annual' 
                ? 'bg-amber-500 text-black' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Anual
            <Badge className="bg-green-500 text-white text-xs">20% OFF</Badge>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentPlan?.plan === plan.id;
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 ${plan.color} bg-gradient-to-b ${plan.bgGradient} p-6 flex flex-col ${
                  plan.recommended ? 'ring-2 ring-amber-500/50 scale-105 z-10' : ''
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-black font-bold px-4 py-1">
                      RECOMENDADO
                    </Badge>
                  </div>
                )}

                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${plan.recommended ? 'bg-amber-500/20' : 'bg-zinc-800'}`}>
                      <Icon className={`w-6 h-6 ${plan.recommended ? 'text-amber-500' : 'text-zinc-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className="text-sm text-zinc-500">{plan.description}</p>
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">${getPrice(plan)}</span>
                    <span className="text-zinc-500">
                      USD / {billingCycle === 'annual' ? 'año' : 'mes'}
                    </span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-green-500 text-sm mt-1">
                      Ahorras ${getSavings(plan)} USD al año
                    </p>
                  )}
                  <p className="text-zinc-500 text-sm mt-2">
                    +${plan.extraUserPrice}/mes por usuario adicional
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-6">
                  {plan.features.map((feature, i) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <li key={i} className={`flex items-start gap-3 ${feature.highlight ? 'text-amber-500' : 'text-zinc-300'}`}>
                        <FeatureIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          feature.highlight ? 'text-amber-500' : plan.recommended ? 'text-amber-500' : 'text-green-500'
                        }`} />
                        <span className="text-sm">{feature.text}</span>
                      </li>
                    );
                  })}
                </ul>

                {/* CTA */}
                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isCurrentPlan || processingPlan === plan.id}
                  className={`w-full h-12 font-bold ${
                    plan.recommended 
                      ? 'bg-amber-500 hover:bg-amber-400 text-black' 
                      : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                  }`}
                  data-testid={`precios-select-${plan.id}`}
                >
                  {isCurrentPlan ? (
                    'Plan Actual'
                  ) : processingPlan === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {user ? 'Seleccionar Plan' : 'Seleccionar Plan'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Trust badges */}
        <div className="mt-16 text-center">
          <p className="text-zinc-500 text-sm mb-6">
            Sin contratos • Cancela en cualquier momento • Datos seguros
          </p>
          <div className="flex justify-center items-center gap-8 text-zinc-600">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm">SSL Encriptado</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span className="text-sm">Multi-tenant Seguro</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="text-sm">Backups Diarios</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="border-t border-zinc-800 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4">
            {[
              {
                q: '¿Qué incluye la prueba gratuita?',
                a: 'Acceso completo al plan Control durante 7 días. Sin necesidad de tarjeta de crédito.'
              },
              {
                q: '¿Puedo cambiar de plan después?',
                a: 'Sí, puedes actualizar o cambiar tu plan en cualquier momento. Los cambios se aplican de inmediato.'
              },
              {
                q: '¿Qué métodos de pago aceptan?',
                a: 'Aceptamos todas las tarjetas de crédito y débito principales a través de Stripe.'
              },
              {
                q: '¿Mis datos están seguros?',
                a: 'Absolutamente. Utilizamos encriptación SSL, arquitectura multi-tenant aislada y backups diarios.'
              },
              {
                q: '¿Puedo agregar más usuarios después?',
                a: 'Sí, puedes agregar usuarios adicionales en cualquier momento por un costo mensual según tu plan.'
              }
            ].map((faq, i) => (
              <div key={i} className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
                <h3 className="text-white font-medium mb-2">{faq.q}</h3>
                <p className="text-zinc-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-zinc-800 py-16 px-4 bg-gradient-to-t from-amber-500/5 to-transparent">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            ¿Listo para tomar el control?
          </h2>
          <p className="text-zinc-400 mb-8">
            Únete a desarrolladores inmobiliarios que ya gestionan sus operaciones con transparencia total.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold h-12 px-8"
          >
            Comenzar Prueba Gratis
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center text-zinc-600 text-sm">
          © {new Date().getFullYear()} Ibañez Desarrollos Inmobiliarios. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
}
